import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  Button,
  Field,
  Input,
  Textarea,
  NativeSelect,
  VStack,
  HStack,
  Box,
  Text,
  Avatar,
  Badge,
  Checkbox,
  IconButton,
  useBreakpointValue,
} from '@chakra-ui/react';
import { tasksAPI, projectsAPI } from '../services/api';
import { Task, User, Column, Comment, TaskAttachment, Project } from '../types';
import { toast } from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { CommentRichEditor } from './CommentRichEditor';
import { sanitizeHtml, isEmptyHtml } from '../utils/sanitizeHtml';
import { hasUnresolvedDependsOn } from '../utils/taskDepends';
import { resolveLogoUrl } from '../utils/logoUrl';
import '../styles/commentContent.css';

const COMPLETION_OPTIONS = Array.from({ length: 11 }, (_, i) => i * 10);

const PANEL_PAD = { base: 3, md: 4, lg: 6 } as const;
const GRID_THREE_COL = { base: '1fr', lg: 'repeat(3, minmax(0, 1fr))' } as const;
const GRID_TWO_COL = { base: '1fr', lg: 'repeat(2, minmax(0, 1fr))' } as const;

function formatFileSize(n: number | undefined): string {
  const s = Number(n) || 0;
  if (s < 1024) return `${s} B`;
  if (s < 1024 * 1024) return `${(s / 1024).toFixed(1)} KB`;
  return `${(s / (1024 * 1024)).toFixed(1)} MB`;
}

function canManageProjectForUser(project: Project | undefined | null, user: User | null | undefined): boolean {
  if (!project || !user?.id) return false;
  const isMasterAdmin = user.role === 'masterAdmin' || user.email === 'admin@admin.com';
  if (isMasterAdmin) return true;
  const creatorId = (project.createdBy as any)?._id || (project.createdBy as any)?.id;
  if (creatorId && creatorId.toString() === user.id) return true;
  const isMember = (project.members || []).some(
    (m: any) => (m._id || m.id)?.toString?.() === user.id,
  );
  if (user.role === 'admin' && isMember) return true;
  return false;
}

function collectTaskParticipantIds(task: Task | null | undefined): string[] {
  const ids: string[] = [];
  if (Array.isArray(task?.participants)) {
    ids.push(
      ...(task!.participants as any[])
        .map((p: any) => (p?._id || p?.id)?.toString?.())
        .filter(Boolean) as string[],
    );
  }
  if (task?.assignedTo) {
    const aId = ((task.assignedTo as any)?._id || (task.assignedTo as any)?.id)?.toString?.();
    if (aId) ids.push(aId);
  }
  return ids;
}

function isUserTaskCreator(task: Task | null | undefined, userId: string | undefined): boolean {
  if (!task || !userId) return false;
  const creatorId = (task.createdBy as any)?._id || (task.createdBy as any)?.id;
  return !!creatorId && creatorId.toString() === userId;
}

function resolveUserAvatarSrc(userLike: any): string | null {
  if (!userLike) return null;
  if (userLike.avatarUrl) return resolveLogoUrl(userLike.avatarUrl);
  const stored = userLike.avatar?.storedFilename;
  if (!stored) return null;
  return resolveLogoUrl(`/api/user-avatars/${encodeURIComponent(String(stored))}`);
}

interface TaskModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  /** Used for delete-task and project-manager rules; omit if unavailable */
  project?: Project | null;
  task?: Task | null;
  onSaved: () => void;
  focusCommentId?: string;
}

const TaskModal: React.FC<TaskModalProps> = ({ open, onClose, projectId, project, task, onSaved, focusCommentId }) => {
  const { user: currentUser } = useAuth();
  const [fullTask, setFullTask] = useState<Task | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<string>('todo');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [completionPercent, setCompletionPercent] = useState(0);
  const [assignedTo, setAssignedTo] = useState('');
  const [members, setMembers] = useState<User[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [commentFileForNew, setCommentFileForNew] = useState<File | null>(null);
  const [commentAttachBusy, setCommentAttachBusy] = useState(false);
  const [pendingCommentIdForFile, setPendingCommentIdForFile] = useState<string | null>(null);
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [editingComment, setEditingComment] = useState<Comment | null>(null);
  const [editEditorKey, setEditEditorKey] = useState(0);
  const [dependsOn, setDependsOn] = useState<string[]>([]);
  const [participants, setParticipants] = useState<string[]>([]);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [finishedColumnId, setFinishedColumnId] = useState<string | null>(null);
  const [mobilePanel, setMobilePanel] = useState<'details' | 'comments'>('details');
  const [commentComposerOpen, setCommentComposerOpen] = useState(false);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const newCommentFileInputRef = useRef<HTMLInputElement>(null);
  const existingCommentFileInputRef = useRef<HTMLInputElement>(null);
  const commentsScrollRef = useRef<HTMLDivElement>(null);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<{ title: string; description: string; status: string; priority: string; completionPercent: number; assignedTo: string; dependsOn: string[]; participants: string[] } | null>(null);

  const loadProjectTasks = useCallback(async () => {
    try {
      const response = await tasksAPI.getByProject(projectId);
      setProjectTasks(response.data);
    } catch (error) {
      console.error('Failed to load project tasks', error);
    }
  }, [projectId]);

  const scrollCommentsToEnd = useCallback(() => {
    requestAnimationFrame(() => {
      const el = commentsScrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  }, []);

  const commentMentionsCurrentUser = useCallback(
    (comment: Comment): boolean => {
      if (!currentUser?.id || !comment?.text) return false;
      const needle = `data-user-id="${currentUser.id}"`;
      const needleAlt = `data-user-id='${currentUser.id}'`;
      return comment.text.includes(needle) || comment.text.includes(needleAlt);
    },
    [currentUser?.id],
  );

  useEffect(() => {
    if (!open || !focusCommentId || comments.length === 0) return;
    setMobilePanel('comments');
    requestAnimationFrame(() => {
      const node = document.getElementById(`comment-${focusCommentId}`);
      node?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }, [open, focusCommentId, comments.length]);

  useEffect(() => {
    if (open) {
      loadProjectMembers();
      loadProjectTasks();
      if (task) {
        loadFullTask();
      } else {
        lastSavedRef.current = null;
        setTitle('');
        setDescription('');
        setPriority('medium');
        setCompletionPercent(0);
        setAssignedTo('');
        setDependsOn([]);
        setFullTask(null);
        setComments([]);
        setCommentFileForNew(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task, loadProjectTasks]);

  useEffect(() => {
    if (open && task) {
      scrollCommentsToEnd();
    }
  }, [open, task?._id, comments.length, scrollCommentsToEnd]);

  const toUserId = (u: any) => String((u?._id ?? u?.id) ?? '').trim() || null;
  const normalizeParticipantIds = (participantsArray: any[], assigned?: any): string[] => {
    const ids: string[] = [];
    if (Array.isArray(participantsArray) && participantsArray.length > 0) {
      participantsArray.forEach((p) => {
        const id = toUserId(p);
        if (id && !ids.includes(id)) ids.push(id);
      });
    }
    if (assigned) {
      const aId = toUserId(assigned);
      if (aId && !ids.includes(aId)) ids.unshift(aId);
    }
    return ids;
  };

  const loadFullTask = async () => {
    if (!task?._id) return;
    try {
      const response = await tasksAPI.getById(task._id);
      const loadedTask = response.data;
      setFullTask(loadedTask);
      setTitle(loadedTask.title);
      setDescription(loadedTask.description || '');
      setStatus(loadedTask.status);
      setPriority(loadedTask.priority);
      setCompletionPercent(loadedTask.completionPercent ?? 0);
      const assignedIds = normalizeParticipantIds(loadedTask.participants || [], loadedTask.assignedTo);
      setParticipants(assignedIds);
      setAssignedTo(assignedIds[0] || '');
      setComments(loadedTask.comments || []);
      const depIds = (loadedTask.dependsOn || []).map((d: any) => d._id || d);
      setDependsOn(depIds);
      lastSavedRef.current = {
        title: loadedTask.title,
        description: loadedTask.description || '',
        status: loadedTask.status,
        priority: loadedTask.priority,
        completionPercent: loadedTask.completionPercent ?? 0,
        assignedTo: assignedIds[0] || '',
        dependsOn: depIds,
        participants: assignedIds,
      };
    } catch (error) {
      console.error('Failed to load task details', error);
      // Fallback to provided task data
      if (task) {
        setTitle(task.title);
        setDescription(task.description || '');
        setStatus(task.status);
        setPriority(task.priority);
        setCompletionPercent(task.completionPercent ?? 0);
        const assignedIds = normalizeParticipantIds((task as any).participants || [], task.assignedTo);
        setParticipants(assignedIds);
        setAssignedTo(assignedIds[0] || '');
        setComments(task.comments || []);
        const depIds = (task.dependsOn || []).map((d: any) => d._id || d);
        setDependsOn(depIds);
        lastSavedRef.current = {
          title: task.title,
          description: task.description || '',
          status: task.status,
          priority: task.priority,
          completionPercent: task.completionPercent ?? 0,
          assignedTo: assignedIds[0] || '',
          dependsOn: depIds,
          participants: assignedIds,
        };
      }
    }
  };

  const loadProjectMembers = async () => {
    try {
      const response = await projectsAPI.getById(projectId);
      setMembers(response.data.members || []);
      setFinishedColumnId(response.data.finishedColumnId ?? null);
      if (response.data.columns && response.data.columns.length > 0) {
        setColumns(response.data.columns.sort((a: Column, b: Column) => a.order - b.order));
        // Set default status to first column if creating new task
        if (!task && response.data.columns.length > 0) {
          setStatus(response.data.columns[0].id);
        }
      } else {
        // Fallback to default columns
        const defaultColumns: Column[] = [
          { id: 'todo', title: 'To Do', color: '#9CA3AF', order: 0 },
          { id: 'in-progress', title: 'In Progress', color: '#3B82F6', order: 1 },
          { id: 'done', title: 'Done', color: '#10B981', order: 2 }
        ];
        setColumns(defaultColumns);
        if (!task) {
          setStatus('todo');
        }
      }
    } catch (error) {
      console.error('Failed to load project members', error);
    }
  };

  const saveTask = useCallback(async (taskData: { title: string; description: string; status: string; priority: string; completionPercent: number; assignedTo: string; dependsOn: string[]; participants: string[] }) => {
    if (!task?._id) return;
    try {
      await tasksAPI.update(task._id, {
        ...taskData,
        project: projectId,
        assignedTo: taskData.assignedTo || undefined,
        participants: taskData.participants || [],
        dependsOn: taskData.dependsOn || [],
      });
      lastSavedRef.current = taskData;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save task',
        status: 'error',
      });
    }
  }, [task?._id, projectId, onSaved]);

  const effectiveTaskForPerms = fullTask || task;
  const participantIdsForEditEarly = collectTaskParticipantIds(effectiveTaskForPerms);
  const isCreatorOfTaskEarly = isUserTaskCreator(effectiveTaskForPerms, currentUser?.id);
  const isOnTaskEarly =
    !!currentUser?.id &&
    (participantIdsForEditEarly.includes(currentUser.id) || isCreatorOfTaskEarly);
  const restrictedEndUser =
    !!task &&
    currentUser?.role === 'endUser' &&
    isOnTaskEarly &&
    !isCreatorOfTaskEarly;
  const canEditForSave =
    !task ||
    currentUser?.role === 'masterAdmin' ||
    currentUser?.email === 'admin@admin.com' ||
    currentUser?.role === 'admin' ||
    restrictedEndUser ||
    (isOnTaskEarly && !restrictedEndUser);

  const canManageTaskAttachmentsEarly =
    !task ||
    currentUser?.role === 'masterAdmin' ||
    currentUser?.email === 'admin@admin.com' ||
    isCreatorOfTaskEarly ||
    isOnTaskEarly;

  const canManageTaskParticipantsEarly =
    !task ||
    currentUser?.role === 'masterAdmin' ||
    currentUser?.email === 'admin@admin.com' ||
    isCreatorOfTaskEarly;

  useEffect(() => {
    if (!task?._id || !title.trim()) return;
    if (!lastSavedRef.current) return;
    if (!canEditForSave) return;
    const current = { title, description, status, priority, completionPercent, assignedTo, dependsOn, participants };
    const last = lastSavedRef.current;
    const depEqual = JSON.stringify([...(last.dependsOn || [])].sort()) === JSON.stringify([...dependsOn].sort());
    if (last && last.title === current.title && last.description === current.description &&
        last.status === current.status && last.priority === current.priority &&
        last.completionPercent === current.completionPercent && last.assignedTo === current.assignedTo && depEqual &&
        JSON.stringify([...(last.participants || [])].sort()) === JSON.stringify([...participants].sort())) {
      return;
    }
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      saveTask(current);
    }, 600);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [task?._id, title, description, status, priority, completionPercent, assignedTo, dependsOn, participants, saveTask, canEditForSave]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: 'Error',
        description: 'Title is required',
        status: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const uniqueParticipants = Array.from(new Set(participants.filter(Boolean)));
      const taskData = {
        title,
        description,
        status,
        priority,
        completionPercent,
        project: projectId,
        assignedTo: (assignedTo || uniqueParticipants[0]) || undefined,
        participants: uniqueParticipants,
      };

      if (task) {
        await tasksAPI.update(task._id, { ...taskData, dependsOn });
        lastSavedRef.current = { title, description, status, priority, completionPercent, assignedTo: taskData.assignedTo || '', dependsOn, participants: uniqueParticipants };
        onSaved();
      } else {
        await tasksAPI.create({ ...taskData, dependsOn });
        toast({ title: 'Success', description: 'Task created', status: 'success' });
        onSaved();
        onClose();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save task',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    
    setLoading(true);
    try {
      await tasksAPI.delete(task._id);
      toast({
        title: 'Success',
        description: 'Task deleted',
        status: 'success',
      });
      onSaved();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to delete task',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    const fileOk = !!(canManageTaskAttachmentsEarly && commentFileForNew);
    if (!task?._id || (isEmptyHtml(newComment) && !fileOk)) return;

    setCommentLoading(true);
    try {
      const response = await tasksAPI.addComment(
        task._id,
        newComment,
        fileOk ? commentFileForNew || undefined : undefined,
      );
      setComments(response.data.comments || []);
      setNewComment('');
      setCommentFileForNew(null);
      setCommentComposerOpen(false);
      scrollCommentsToEnd();
      toast({
        title: 'Success',
        description: 'Comment added',
        status: 'success',
      });
      // Reload full task to get updated data
      await loadFullTask();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add comment',
        status: 'error',
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const handleAttachmentChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !task?._id) return;
    setAttachmentUploading(true);
    try {
      await tasksAPI.uploadAttachment(task._id, file);
      await loadFullTask();
      onSaved();
      toast({ title: 'Success', description: 'Attachment uploaded', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Upload failed',
        status: 'error',
      });
    } finally {
      setAttachmentUploading(false);
    }
  };

  const handleDownloadAttachment = async (att: TaskAttachment) => {
    if (!task?._id) return;
    try {
      const res = await tasksAPI.downloadAttachmentBlob(task._id, att._id);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error', description: 'Download failed', status: 'error' });
    }
  };

  const handleDeleteAttachment = async (att: TaskAttachment) => {
    if (!task?._id) return;
    try {
      await tasksAPI.deleteAttachment(task._id, att._id);
      await loadFullTask();
      onSaved();
      toast({ title: 'Success', description: 'Attachment removed', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete attachment',
        status: 'error',
      });
    }
  };

  const openExistingCommentFilePicker = (commentId: string) => {
    setPendingCommentIdForFile(commentId);
    requestAnimationFrame(() => existingCommentFileInputRef.current?.click());
  };

  const onNewCommentFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (file) setCommentFileForNew(file);
  };

  const onExistingCommentFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const cid = pendingCommentIdForFile;
    setPendingCommentIdForFile(null);
    if (!file || !task?._id || !cid) return;
    setCommentAttachBusy(true);
    try {
      await tasksAPI.uploadCommentAttachment(task._id, cid, file);
      await loadFullTask();
      onSaved();
      toast({ title: 'Success', description: 'File attached to comment', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Upload failed',
        status: 'error',
      });
    } finally {
      setCommentAttachBusy(false);
    }
  };

  const handleDownloadCommentAttachment = async (commentId: string, att: TaskAttachment) => {
    if (!task?._id || !att._id) return;
    try {
      const res = await tasksAPI.downloadCommentAttachmentBlob(task._id, commentId, att._id);
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.originalName || 'download';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      toast({ title: 'Error', description: 'Download failed', status: 'error' });
    }
  };

  const handleDeleteCommentAttachment = async (commentId: string, att: TaskAttachment) => {
    if (!task?._id || !att._id) return;
    try {
      await tasksAPI.deleteCommentAttachment(task._id, commentId, att._id);
      await loadFullTask();
      onSaved();
      toast({ title: 'Success', description: 'Attachment removed', status: 'success' });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete attachment',
        status: 'error',
      });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentTask = effectiveTaskForPerms;
  const isCreatorOfTask = isCreatorOfTaskEarly;

  const isAdminRole = currentUser?.role === 'admin';
  const isMasterAdmin =
    currentUser?.role === 'masterAdmin' || currentUser?.email === 'admin@admin.com';

  const canComment = !task || isMasterAdmin || isAdminRole || isOnTaskEarly;

  const getCommentAuthorId = (comment: Comment): string | null => {
    const author = comment.createdBy;
    if (!author) return null;
    if (typeof author === 'string') return author;
    return (author.id || author._id)?.toString?.() || null;
  };

  const canEditComment = (comment: Comment): boolean => {
    if (!canComment || !currentUser?.id) return false;
    return getCommentAuthorId(comment) === currentUser.id;
  };

  const startEditComment = (comment: Comment) => {
    const latest =
      comments.find((c) => c._id === comment._id) || comment;
    setEditingComment(latest);
    setEditingCommentId(latest._id);
    setEditCommentText(latest.text || '');
    setEditEditorKey((k) => k + 1);
  };

  const cancelEditComment = () => {
    setEditingComment(null);
    setEditingCommentId(null);
    setEditCommentText('');
  };

  const handleUpdateComment = async () => {
    if (!task?._id || !editingCommentId || isEmptyHtml(editCommentText)) return;

    setCommentLoading(true);
    try {
      const response = await tasksAPI.updateComment(task._id, editingCommentId, editCommentText);
      const nextComments = response.data.comments || [];
      setComments(nextComments);
      toast({
        title: 'Success',
        description: 'Comment updated',
        status: 'success',
      });
      cancelEditComment();
      await loadFullTask();
      onSaved();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to update comment',
        status: 'error',
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const restoreCommentVersion = (versionText: string) => {
    setEditCommentText(versionText || '');
    setEditEditorKey((k) => k + 1);
  };

  const canEditFull =
    !task ||
    isMasterAdmin ||
    isAdminRole ||
    (isOnTaskEarly && !restrictedEndUser);

  const canChangeStatus = canComment;

  const statusBlockedByDepends =
    !!task &&
    hasUnresolvedDependsOn(currentTask, projectTasks, finishedColumnId, columns);

  const canManageTaskAttachmentsUi = canManageTaskAttachmentsEarly;
  const canManageTaskParticipantsUi = canManageTaskParticipantsEarly;

  const canDeleteTaskUi =
    !!task &&
    (isMasterAdmin || isCreatorOfTask || canManageProjectForUser(project ?? null, currentUser));

  const canRemoveCommentAttachment = (): boolean =>
    !!(task && (isMasterAdmin || isCreatorOfTask));

  const mentionUsers = members
    .map((m) => {
      const id = ((m as any).id || (m as any)._id)?.toString?.();
      if (!id || !m.name) return null;
      return { id, name: m.name, email: m.email };
    })
    .filter(Boolean) as { id: string; name: string; email?: string }[];

  const isFullScreenLayout = !!task;
  const isMobileLayout = useBreakpointValue({ base: true, lg: false }) ?? true;
  const commentEditorMinHeight = useBreakpointValue({ base: '72px', lg: '100px' }) ?? '72px';

  useEffect(() => {
    if (!open) {
      setMobilePanel('details');
      setCommentComposerOpen(false);
      return;
    }
    if (!isMobileLayout) return;
    setMobilePanel('details');
    setCommentComposerOpen(false);
    const timer = window.setTimeout(() => {
      const active = document.activeElement as HTMLElement | null;
      if (active && active !== document.body) active.blur();
    }, 0);
    return () => clearTimeout(timer);
  }, [open, task?._id, isMobileLayout]);

  return (
    <>
    <Dialog.Root open={open} onOpenChange={(e: { open: boolean }) => !e.open && onClose()} size="full">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          maxW="100vw"
          maxH={{ base: '100dvh', lg: '100vh' }}
          w="100vw"
          h={{ base: '100dvh', lg: '100vh' }}
          borderRadius={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Dialog.Header
            position="relative"
            w="100%"
            flexShrink={0}
            borderBottomWidth={1}
            borderColor="gray.200"
            px={PANEL_PAD}
            py={{ base: 3, lg: 4 }}
            pr={{ base: 14, lg: 16 }}
          >
            <Box minW={0}>
              <Dialog.Title fontSize={{ base: 'md', lg: 'lg' }} lineClamp={2}>
                {task ? (title.trim() || 'Task') : 'New task'}
              </Dialog.Title>
              {task && !canComment && (
                <Badge colorScheme="gray" mt={1} fontSize="xs" w="fit-content">View only</Badge>
              )}
            </Box>
            <IconButton
              aria-label="Close"
              variant="ghost"
              size="sm"
              position="absolute"
              top={{ base: 2, lg: 3 }}
              right={{ base: 2, lg: 3 }}
              minW="36px"
              h="36px"
              onClick={onClose}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </IconButton>
          </Dialog.Header>

          {isFullScreenLayout && isMobileLayout && (
            <HStack
              flexShrink={0}
              borderBottomWidth={1}
              borderColor="gray.200"
              px={PANEL_PAD}
              py={2}
              gap={2}
              bg="white"
            >
              <Button
                flex={1}
                size="sm"
                variant={mobilePanel === 'details' ? 'solid' : 'outline'}
                colorScheme={mobilePanel === 'details' ? 'blue' : 'gray'}
                onClick={() => setMobilePanel('details')}
              >
                Details
              </Button>
              <Button
                flex={1}
                size="sm"
                variant={mobilePanel === 'comments' ? 'solid' : 'outline'}
                colorScheme={mobilePanel === 'comments' ? 'blue' : 'gray'}
                onClick={() => {
                  setMobilePanel('comments');
                  scrollCommentsToEnd();
                }}
              >
                Comments ({comments.length})
              </Button>
            </HStack>
          )}

          {isFullScreenLayout ? (
            <Box
              flex={1}
              display="flex"
              flexDirection={{ base: 'column', lg: 'row' }}
              minH={0}
              overflow="hidden"
            >
              {/* Leva kolona: naziv, opis, status, itd. */}
              <Box
                flex={{ base: 1, lg: 1 }}
                display={{ base: mobilePanel === 'details' ? 'flex' : 'none', lg: 'flex' }}
                flexDirection="column"
                borderRightWidth={{ base: 0, lg: 1 }}
                borderBottomWidth={{ base: 0, lg: 0 }}
                borderColor="gray.200"
                minW={0}
                minH={0}
              >
                <Box flex={1} overflowY="auto" p={PANEL_PAD} minH={0}>
                  <VStack gap={{ base: 4, lg: 5 }} align="stretch">
                  <Field.Root required>
                    <Field.Label fontSize={{ base: 'sm', lg: 'md' }}>Name</Field.Label>
                    <Input
                      value={title}
                      onChange={(e) => canEditFull && setTitle(e.target.value)}
                      placeholder="Task name"
                      size={{ base: 'md', lg: 'lg' }}
                      fontWeight="semibold"
                      readOnly={!canEditFull}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize={{ base: 'sm', lg: 'md' }}>Description</Field.Label>
                    <Textarea
                      value={description}
                      onChange={(e) => canEditFull && setDescription(e.target.value)}
                      placeholder="Task description"
                      rows={4}
                      readOnly={!canEditFull}
                      fontSize={{ base: 'sm', lg: 'md' }}
                    />
                  </Field.Root>
                  <Box display="grid" gridTemplateColumns={GRID_THREE_COL} gap={{ base: 3, lg: 4 }} w="100%">
                    <Field.Root minW={0}>
                      <Field.Label fontWeight="medium" fontSize={{ base: 'sm', lg: 'md' }}>Status</Field.Label>
                      <NativeSelect.Root disabled={!canChangeStatus || statusBlockedByDepends}>
                        <NativeSelect.Field
                          value={status}
                          onChange={(e: any) =>
                            canChangeStatus && !statusBlockedByDepends && setStatus(e.target.value)
                          }
                          mt={1}
                          borderRadius="lg"
                        >
                          {columns.map((column) => (
                            <option key={column.id} value={column.id}>{column.title}</option>
                          ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                      {statusBlockedByDepends && (
                        <Text fontSize="xs" color="orange.600" mt={1}>
                          Status is locked until every task listed under Depends on is in the Done column.
                        </Text>
                      )}
                    </Field.Root>
                    <Field.Root minW={0}>
                      <Field.Label fontWeight="medium" fontSize={{ base: 'sm', lg: 'md' }}>Priority</Field.Label>
                      <NativeSelect.Root disabled={!canEditFull}>
                        <NativeSelect.Field value={priority} onChange={(e: any) => canEditFull && setPriority(e.target.value as any)} mt={1} borderRadius="lg">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root minW={0}>
                      <Field.Label fontWeight="medium" fontSize={{ base: 'sm', lg: 'md' }}>Completion</Field.Label>
                      <NativeSelect.Root disabled={!canChangeStatus}>
                        <NativeSelect.Field
                          value={String(completionPercent)}
                          onChange={(e: any) =>
                            canChangeStatus && setCompletionPercent(Number(e.target.value))
                          }
                          mt={1}
                          borderRadius="lg"
                        >
                          {COMPLETION_OPTIONS.map((pct) => (
                            <option key={pct} value={pct}>{pct}%</option>
                          ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                      <Box mt={2} h="6px" borderRadius="full" bg="gray.200" overflow="hidden">
                        <Box h="100%" w={`${completionPercent}%`} bg="blue.500" transition="width 0.2s" />
                      </Box>
                    </Field.Root>
                  </Box>
                  <Box display="grid" gridTemplateColumns={GRID_TWO_COL} gap={{ base: 4, lg: 6 }} w="100%">
                    <Field.Root minW={0}>
                      <Field.Label fontWeight="medium" fontSize={{ base: 'sm', lg: 'md' }}>Assigned</Field.Label>
                      <Box
                        mt={1}
                        w="100%"
                        borderWidth={1}
                        borderRadius="lg"
                        borderColor="gray.200"
                        maxH="210px"
                        overflowY="auto"
                        bg="gray.50"
                        _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                      >
                        <VStack align="stretch" gap={0} p={1}>
                          {members.length === 0 && (
                            <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                              No project members.
                            </Text>
                          )}
                          {members.map((member) => {
                            const id = String((member as any)._id ?? (member as any).id ?? '');
                            if (!id) return null;
                            const checked = participants.includes(id);
                            const handleToggle = () => {
                              if (!canManageTaskParticipantsUi) return;
                              const next = checked
                                ? participants.filter((x) => x !== id)
                                : [...participants, id];
                              setParticipants(next);
                              setAssignedTo(next[0] || '');
                            };
                            return (
                              <Box
                                key={id}
                                px={3}
                                py={2}
                                borderRadius="md"
                                cursor={canManageTaskParticipantsUi ? 'pointer' : 'default'}
                                bg={checked ? 'purple.100' : 'transparent'}
                                _hover={canManageTaskParticipantsUi ? { bg: checked ? 'purple.200' : 'gray.100' } : undefined}
                                transition="background 0.15s"
                                onClick={handleToggle}
                              >
                                <Checkbox.Root checked={checked} cursor={canManageTaskParticipantsUi ? 'pointer' : 'default'} pointerEvents="none">
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control />
                                  <Checkbox.Label ml={2} fontWeight={checked ? 'medium' : 'normal'}>
                                    {member.name}
                                    {checked && participants[0] === id && (
                                      <Badge ml={2} size="sm" colorScheme="purple" variant="subtle">
                                        Main
                                      </Badge>
                                    )}
                                  </Checkbox.Label>
                                </Checkbox.Root>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={2} display={{ base: 'none', md: 'block' }}>
                        The first selected will be the main assignee. Click a row to select.
                      </Text>
                    </Field.Root>

                    <Field.Root minW={0}>
                      <Field.Label fontWeight="medium" fontSize={{ base: 'sm', lg: 'md' }}>Depends on</Field.Label>
                      <NativeSelect.Root disabled={!canEditFull}>
                        <NativeSelect.Field
                          value=""
                          mt={1}
                          borderRadius="lg"
                          onChange={(e: any) => {
                            if (!canEditFull) return;
                            const id = e.target.value;
                            if (id && !dependsOn.includes(id)) setDependsOn([...dependsOn, id]);
                          }}
                        >
                          <option value="">Add task...</option>
                          {projectTasks
                            .filter(
                              (t) =>
                                t._id !== task?._id &&
                                !dependsOn.includes(t._id) &&
                                t.status !== finishedColumnId
                            )
                            .map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.title}
                              </option>
                            ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                      <Text fontSize="xs" color="gray.500" mt={1} display={{ base: 'none', md: 'block' }}>
                        Tasks you must complete before this one.
                      </Text>
                    </Field.Root>
                  </Box>
                  {dependsOn.length > 0 && (
                    <Box mt={3} p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200" bg="gray.50">
                      <VStack gap={2} align="stretch">
                        {dependsOn.map((id) => {
                          const t =
                            projectTasks.find((x) => x._id === id) ||
                            fullTask?.dependsOn?.find((d: any) => (d._id || d) === id);
                          const statusCol = t?.status === 'done' ? 'green' : 'yellow';
                          return (
                            <HStack key={id} justifyContent="space-between" fontSize="sm">
                              <HStack gap={2}>
                                <Badge colorScheme={statusCol} size="sm" variant="subtle">
                                  {t?.title || id.slice(-6)}
                                </Badge>
                              </HStack>
                              {canEditFull && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="gray"
                                  onClick={() => setDependsOn(dependsOn.filter((d) => d !== id))}
                                  aria-label="Ukloni"
                                >
                                  ×
                                </Button>
                              )}
                            </HStack>
                          );
                        })}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        This task can only be done after the listed tasks are completed.
                      </Text>
                    </Box>
                  )}

                  {task && (
                    <Box pt={2} borderTopWidth={1} borderColor="gray.200">
                      <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                        Attachments
                      </Text>
                      <input
                        ref={attachmentInputRef}
                        type="file"
                        style={{ display: 'none' }}
                        onChange={handleAttachmentChange}
                      />
                      {canManageTaskAttachmentsUi && (
                        <Button
                          size="sm"
                          variant="outline"
                          mb={3}
                          loading={attachmentUploading}
                          onClick={() => attachmentInputRef.current?.click()}
                        >
                          Add file
                        </Button>
                      )}
                      <Text fontSize="xs" color="gray.500" mb={3} display={{ base: 'none', md: 'block' }}>
                        Files are stored on the server. Max 2 MB per file (UPLOAD_MAX_FILE_MB).
                      </Text>
                      {(!fullTask?.attachments || fullTask.attachments.length === 0) ? (
                        <Text fontSize="sm" color="gray.500">
                          No attachments yet.
                        </Text>
                      ) : (
                        <VStack gap={2} align="stretch">
                          {(fullTask?.attachments ?? []).map((att) => (
                            <Box
                              key={att._id}
                              p={3}
                              borderWidth={1}
                              borderColor="gray.200"
                              borderRadius="lg"
                              bg="white"
                            >
                              <Text fontSize="sm" fontWeight="medium" truncate mb={1}>
                                {att.originalName}
                              </Text>
                              <Text fontSize="xs" color="gray.500" mb={2}>
                                {formatFileSize(att.size)}
                                {(att.uploadedBy as User)?.name
                                  ? ` · ${(att.uploadedBy as User).name}`
                                  : ''}
                              </Text>
                              <HStack gap={2} flexWrap="wrap">
                                <Button size="xs" variant="outline" flex={{ base: 1, sm: 'none' }} onClick={() => handleDownloadAttachment(att)}>
                                  Download
                                </Button>
                                {canManageTaskAttachmentsUi && (
                                  <Button
                                    size="xs"
                                    variant="ghost"
                                    colorScheme="red"
                                    flex={{ base: 1, sm: 'none' }}
                                    onClick={() => handleDeleteAttachment(att)}
                                  >
                                    Remove
                                  </Button>
                                )}
                              </HStack>
                            </Box>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  )}

                  {currentTask && (
                    <Box pt={4} borderTopWidth={1} borderColor="gray.200" display={{ base: 'none', md: 'block' }}>
                      <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={3}>Information</Text>
                      <VStack gap={2} align="stretch">
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" color="gray.600">Created by:</Text>
                          <Text fontSize="sm" fontWeight="medium">{currentTask.createdBy?.name || '—'}</Text>
                        </HStack>
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" color="gray.600">Created:</Text>
                          <Text fontSize="sm">{formatDate(currentTask.createdAt)}</Text>
                        </HStack>
                        {currentTask.updatedAt && currentTask.updatedAt !== currentTask.createdAt && (
                          <HStack justifyContent="space-between">
                            <Text fontSize="sm" color="gray.600">Modified:</Text>
                            <Text fontSize="sm">{formatDate(currentTask.updatedAt)}</Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  )}
                  </VStack>
                </Box>

                <HStack
                  gap={2}
                  p={PANEL_PAD}
                  pt={3}
                  justifyContent="flex-end"
                  flexShrink={0}
                  borderTopWidth={1}
                  borderColor="gray.100"
                  flexWrap="wrap"
                >
                  {task && canDeleteTaskUi && (
                    <Button
                      colorScheme="red"
                      variant="outline"
                      onClick={handleDelete}
                      loading={loading}
                      size="sm"
                      w={{ base: '100%', lg: 'auto' }}
                    >
                      Delete task
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onSaved();
                      onClose();
                    }}
                    size="sm"
                    display={{ base: 'none', lg: 'inline-flex' }}
                  >
                    Close
                  </Button>
                  {!task && (
                    <Button colorScheme="blue" onClick={handleSubmit} loading={loading} size="sm">
                      Create
                    </Button>
                  )}
                </HStack>
              </Box>

              {/* Komentari — uvek pri dnu (mobil) / desno (desktop) */}
              <Box
                flex={{ base: 1, lg: 1 }}
                display={{ base: mobilePanel === 'comments' ? 'flex' : 'none', lg: 'flex' }}
                flexDirection="column"
                minW={0}
                minH={0}
                bg="gray.50"
                flexShrink={0}
                borderTopWidth={{ base: 0, lg: 0 }}
                borderColor="gray.200"
              >
                <Box px={PANEL_PAD} py={{ base: 2.5, lg: 4 }} borderBottomWidth={1} borderColor="gray.200" flexShrink={0}>
                  <Text fontSize={{ base: 'sm', lg: 'lg' }} fontWeight="semibold">
                    Comments ({comments.length})
                  </Text>
                </Box>
                <Box ref={commentsScrollRef} flex={1} overflowY="auto" p={PANEL_PAD} minH={0}>
                  <VStack gap={{ base: 3, lg: 4 }} align="stretch">
                    {comments.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
                        No comments. Be the first to comment.
                      </Text>
                    ) : (
                      comments.map((comment) => {
                        const mentionsMe = commentMentionsCurrentUser(comment);
                        const isFocused = focusCommentId === comment._id;
                        return (
                        <Box
                          key={comment._id}
                          id={`comment-${comment._id}`}
                          p={{ base: 3, lg: 4 }}
                          bg={isFocused || mentionsMe ? 'blue.50' : 'white'}
                          borderRadius="md"
                          borderWidth={isFocused ? 2 : 1}
                          borderColor={isFocused ? 'blue.400' : mentionsMe ? 'blue.200' : 'gray.200'}
                        >
                          <HStack justifyContent="space-between" align="flex-start" mb={2} gap={2}>
                            <HStack gap={2} minW={0} flex={1}>
                              {resolveUserAvatarSrc(comment.createdBy) ? (
                                <img
                                  src={resolveUserAvatarSrc(comment.createdBy) || ''}
                                  alt={comment.createdBy?.name || 'User avatar'}
                                  style={{
                                    width: '32px',
                                    height: '32px',
                                    borderRadius: '9999px',
                                    objectFit: 'cover',
                                    border: '1px solid #e5e7eb',
                                  }}
                                />
                              ) : (
                                <Avatar.Root size="sm">
                                  <Avatar.Fallback bg="gray.200" color="black" fontWeight="bold">
                                    {comment.createdBy?.name?.charAt(0).toUpperCase() || 'U'}
                                  </Avatar.Fallback>
                                </Avatar.Root>
                              )}
                              <Box minW={0}>
                                <Text fontSize="sm" fontWeight="medium" lineClamp={1}>{comment.createdBy?.name || 'Unknown'}</Text>
                                <Text fontSize="xs" color="gray.500">
                                  {formatDate(comment.createdAt)}
                                  {(comment.versions?.length ?? 0) > 0 ||
                                  (comment.updatedAt &&
                                    new Date(comment.updatedAt).getTime() > new Date(comment.createdAt).getTime())
                                    ? ' · edited'
                                    : ''}
                                </Text>
                              </Box>
                            </HStack>
                            {canEditComment(comment) && (
                              <Button
                                size="xs"
                                variant="ghost"
                                flexShrink={0}
                                onClick={() => startEditComment(comment)}
                                disabled={commentLoading}
                              >
                                Edit
                              </Button>
                            )}
                          </HStack>
                          {comment.text && comment.text.includes('<') ? (
                            <div
                              className="comment-html-content"
                              style={{ fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.text) }}
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                              {comment.text}
                            </Text>
                          )}
                          {(comment.attachments?.length ?? 0) > 0 && (
                            <VStack gap={2} align="stretch" mt={3} pt={3} borderTopWidth={1} borderColor="gray.100">
                              <Text fontSize="xs" fontWeight="medium" color="gray.500">
                                Attachments
                              </Text>
                              {(comment.attachments ?? []).map((att) => (
                                <HStack
                                  key={att._id}
                                  justifyContent="space-between"
                                  gap={2}
                                  flexWrap="wrap"
                                  p={2}
                                  borderWidth={1}
                                  borderColor="gray.100"
                                  borderRadius="md"
                                  bg="gray.50"
                                >
                                  <Box minW={0}>
                                    <Text fontSize="xs" fontWeight="medium" truncate>
                                      {att.originalName}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {formatFileSize(att.size)}
                                      {(att.uploadedBy as User)?.name
                                        ? ` · ${(att.uploadedBy as User).name}`
                                        : ''}
                                    </Text>
                                  </Box>
                                  <HStack gap={1} flexShrink={0}>
                                    <Button
                                      size="xs"
                                      variant="outline"
                                      onClick={() => handleDownloadCommentAttachment(comment._id, att)}
                                    >
                                      Download
                                    </Button>
                                    {canRemoveCommentAttachment() && (
                                      <Button
                                        size="xs"
                                        variant="ghost"
                                        colorScheme="red"
                                        onClick={() => handleDeleteCommentAttachment(comment._id, att)}
                                      >
                                        Remove
                                      </Button>
                                    )}
                                  </HStack>
                                </HStack>
                              ))}
                            </VStack>
                          )}
                          {canManageTaskAttachmentsUi && (
                            <HStack mt={2}>
                              <Button
                                size="xs"
                                variant="outline"
                                loading={commentAttachBusy}
                                onClick={() => openExistingCommentFilePicker(comment._id)}
                              >
                                Add file
                              </Button>
                            </HStack>
                          )}
                        </Box>
                        );
                      })
                    )}
                  </VStack>
                </Box>
                {canComment && (
                <Box p={PANEL_PAD} borderTopWidth={1} borderColor="gray.200" flexShrink={0} bg="white" width="100%">
                  {isMobileLayout && !commentComposerOpen ? (
                    <Button
                      w="full"
                      variant="outline"
                      size="md"
                      onClick={() => setCommentComposerOpen(true)}
                    >
                      Write a comment...
                    </Button>
                  ) : (
                  <VStack gap={{ base: 2, lg: 3 }} align="stretch" width="100%">
                    {isMobileLayout && (
                      <Button
                        size="xs"
                        variant="ghost"
                        alignSelf="flex-start"
                        onClick={() => {
                          setCommentComposerOpen(false);
                          setNewComment('');
                          setCommentFileForNew(null);
                          (document.activeElement as HTMLElement | null)?.blur?.();
                        }}
                      >
                        Cancel
                      </Button>
                    )}
                    <Field.Root width="100%">
                      <Field.Label fontSize={{ base: 'sm', lg: 'md' }} display={{ base: commentComposerOpen || !isMobileLayout ? 'block' : 'none', lg: 'block' }}>
                        <Box as="span" display={{ base: 'inline', lg: 'none' }}>Add comment</Box>
                        <Box as="span" display={{ base: 'none', lg: 'inline' }}>Add comment (Ctrl+B bold, Ctrl+I italic, Ctrl+U underline)</Box>
                      </Field.Label>
                      <CommentRichEditor
                        value={newComment}
                        onChange={setNewComment}
                        placeholder="Write a comment... Use @ to mention someone"
                        minHeight={commentEditorMinHeight}
                        disabled={commentLoading}
                        requireTapToEdit={isMobileLayout}
                        mentionUsers={mentionUsers}
                      />
                    </Field.Root>
                    <input
                      ref={newCommentFileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={onNewCommentFileSelected}
                    />
                    <input
                      ref={existingCommentFileInputRef}
                      type="file"
                      style={{ display: 'none' }}
                      onChange={onExistingCommentFileSelected}
                    />
                    {canManageTaskAttachmentsUi && (
                      <HStack gap={2} flexWrap="wrap" alignItems="center">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => newCommentFileInputRef.current?.click()}
                          disabled={commentLoading}
                        >
                          Attach file
                        </Button>
                        {commentFileForNew && (
                          <HStack gap={2} alignItems="center">
                            <Text fontSize="xs" color="gray.600" truncate maxW="200px">
                              {commentFileForNew.name}
                            </Text>
                            <Button
                              size="xs"
                              variant="ghost"
                              onClick={() => setCommentFileForNew(null)}
                              disabled={commentLoading}
                            >
                              Clear
                            </Button>
                          </HStack>
                        )}
                        <Text fontSize="xs" color="gray.500">
                          Max 2 MB
                        </Text>
                      </HStack>
                    )}
                    <Button
                      colorScheme="blue"
                      onClick={handleAddComment}
                      loading={commentLoading}
                      disabled={
                        isEmptyHtml(newComment) &&
                        !(canManageTaskAttachmentsEarly && commentFileForNew)
                      }
                      size="sm"
                      w={{ base: 'full', lg: 'auto' }}
                      alignSelf={{ base: 'stretch', lg: 'flex-start' }}
                    >
                      Add comment
                    </Button>
                  </VStack>
                  )}
                </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box flex={1} display="flex" flexDirection="column" minH={0}>
              <Dialog.Body flex={1} overflowY="auto" minH={0} p={PANEL_PAD}>
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label fontSize="sm">Name</Field.Label>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task name" size="md" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label fontSize="sm">Description</Field.Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={4} fontSize="sm" />
                  </Field.Root>
                  <Box display="grid" gridTemplateColumns={GRID_THREE_COL} gap={3} w="100%">
                    <Field.Root minW={0}>
                      <Field.Label fontSize="sm">Status</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field value={status} onChange={(e: any) => setStatus(e.target.value)}>
                          {columns.map((col) => (<option key={col.id} value={col.id}>{col.title}</option>))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root minW={0}>
                      <Field.Label fontSize="sm">Priority</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field value={priority} onChange={(e: any) => setPriority(e.target.value as any)}>
                          <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root minW={0}>
                      <Field.Label fontSize="sm">Completion</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value={String(completionPercent)}
                          onChange={(e: any) => setCompletionPercent(Number(e.target.value))}
                        >
                          {COMPLETION_OPTIONS.map((pct) => (
                            <option key={pct} value={pct}>{pct}%</option>
                          ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                  </Box>
                  <Box display="grid" gridTemplateColumns={GRID_TWO_COL} gap={4} w="100%">
                    <Field.Root minW={0}>
                      <Field.Label fontWeight="medium" fontSize="sm">Assigned</Field.Label>
                      <Box
                        mt={1}
                        w="100%"
                        borderWidth={1}
                        borderRadius="lg"
                        borderColor="gray.200"
                        maxH="210px"
                        overflowY="auto"
                        bg="gray.50"
                        p={1}
                      >
                        <VStack align="stretch" gap={0}>
                          {members.length === 0 && (
                            <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                              No project members.
                            </Text>
                          )}
                          {members.map((m) => {
                            const id = String((m as any)._id ?? (m as any).id ?? '');
                            if (!id) return null;
                            const checked = participants.includes(id);
                            const handleToggle = () => {
                              const next = checked
                                ? participants.filter((x) => x !== id)
                                : [...participants, id];
                              setParticipants(next);
                              setAssignedTo(next[0] || '');
                            };
                            return (
                              <Box
                                key={id}
                                px={3}
                                py={2}
                                borderRadius="md"
                                cursor={canManageTaskParticipantsUi ? 'pointer' : 'default'}
                                bg={checked ? 'purple.100' : 'transparent'}
                                _hover={canManageTaskParticipantsUi ? { bg: checked ? 'purple.200' : 'gray.100' } : undefined}
                                transition="background 0.15s"
                                onClick={canManageTaskParticipantsUi ? handleToggle : undefined}
                              >
                                <Checkbox.Root checked={checked} cursor={canManageTaskParticipantsUi ? 'pointer' : 'default'} pointerEvents="none">
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control />
                                  <Checkbox.Label ml={2} fontWeight={checked ? 'medium' : 'normal'}>
                                    {m.name}
                                    {checked && participants[0] === id && (
                                      <Badge ml={2} size="sm" colorScheme="purple" variant="subtle">
                                        Main
                                      </Badge>
                                    )}
                                  </Checkbox.Label>
                                </Checkbox.Root>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        The first selected will be the main assignee. Click a row to select.
                      </Text>
                    </Field.Root>
                    <Field.Root minW={0}>
                      <Field.Label fontSize="sm">Depends on</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value=""
                          onChange={(e: any) => {
                            const id = e.target.value;
                            if (id && !dependsOn.includes(id)) setDependsOn([...dependsOn, id]);
                          }}
                        >
                          <option value="">Add task...</option>
                          {projectTasks
                            .filter(
                              (t) =>
                                !dependsOn.includes(t._id) &&
                                t.status !== finishedColumnId
                            )
                            .map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.title}
                              </option>
                            ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                  </Box>
                  {dependsOn.length > 0 && (
                    <Box mt={2} p={3} borderRadius="md" borderWidth={1} borderColor="gray.200" bg="gray.50">
                      <VStack gap={2} align="stretch">
                        {dependsOn.map((id) => {
                          const t = projectTasks.find((x) => x._id === id);
                          const statusCol = t?.status === 'done' ? 'green' : 'yellow';
                          return (
                            <HStack key={id} justifyContent="space-between" fontSize="sm">
                              <Badge colorScheme={statusCol} size="sm" variant="subtle">
                                {t?.title || id.slice(-6)}
                              </Badge>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                onClick={() => setDependsOn(dependsOn.filter((d) => d !== id))}
                                aria-label="Ukloni"
                              >
                                ×
                              </Button>
                            </HStack>
                          );
                        })}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        This task can only be done after the listed tasks are completed.
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer flexShrink={0} borderTopWidth={1} borderColor="gray.100" p={PANEL_PAD}>
                <HStack justifyContent="flex-end" gap={2} w="full" flexDirection={{ base: 'column-reverse', sm: 'row' }}>
                  <Button variant="ghost" onClick={onClose} w={{ base: 'full', sm: 'auto' }}>Cancel</Button>
                  <Button colorScheme="blue" onClick={handleSubmit} loading={loading} w={{ base: 'full', sm: 'auto' }}>Create</Button>
                </HStack>
              </Dialog.Footer>
            </Box>
          )}
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>

    <Dialog.Root
      open={!!editingComment}
      onOpenChange={(e: { open: boolean }) => {
        if (!e.open) cancelEditComment();
      }}
      size="lg"
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          maxW="640px"
          w="95vw"
          maxH="90dvh"
          borderRadius="xl"
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Dialog.Header borderBottomWidth={1} borderColor="gray.100" px={5} py={4}>
            <Dialog.Title fontSize="lg">Edit comment</Dialog.Title>
            <Dialog.CloseTrigger asChild>
              <IconButton
                aria-label="Close"
                variant="ghost"
                size="sm"
                position="absolute"
                top={3}
                right={3}
              >
                <svg width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </IconButton>
            </Dialog.CloseTrigger>
          </Dialog.Header>
          <Dialog.Body px={5} py={4} overflowY="auto" flex={1} minH={0}>
            <VStack align="stretch" gap={5}>
              <Box>
                <Text fontSize="sm" fontWeight="medium" color="gray.700" mb={2}>
                  Current version
                </Text>
                <CommentRichEditor
                  key={`edit-comment-${editingCommentId}-${editEditorKey}`}
                  value={editCommentText}
                  onChange={setEditCommentText}
                  placeholder="Edit comment... Use @ to mention someone"
                  minHeight="100px"
                  disabled={commentLoading}
                  mentionUsers={mentionUsers}
                />
              </Box>

              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.800" mb={1}>
                  Previous versions
                </Text>
                <Text fontSize="xs" color="gray.500" mb={3}>
                  Older text is kept each time you save. Restore fills the editor — save to apply.
                </Text>
                {(editingComment?.versions?.length ?? 0) === 0 ? (
                  <Box
                    p={4}
                    borderWidth={1}
                    borderStyle="dashed"
                    borderColor="gray.200"
                    borderRadius="lg"
                    bg="gray.50"
                  >
                    <Text fontSize="sm" color="gray.500">
                      No previous versions yet.
                    </Text>
                  </Box>
                ) : (
                  <VStack align="stretch" gap={3}>
                    {[...(editingComment?.versions || [])]
                      .slice()
                      .reverse()
                      .map((version, index) => (
                        <Box
                          key={version._id || `${version.editedAt}-${index}`}
                          p={3}
                          borderWidth={1}
                          borderColor="gray.200"
                          borderRadius="lg"
                          bg="white"
                        >
                          <HStack justifyContent="space-between" align="flex-start" mb={2} gap={2}>
                            <Box minW={0}>
                              <Text fontSize="xs" fontWeight="medium" color="gray.700">
                                Version {(editingComment?.versions?.length || 0) - index}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {formatDate(version.editedAt)}
                                {version.editedBy?.name ? ` · ${version.editedBy.name}` : ''}
                              </Text>
                            </Box>
                            <Button
                              size="xs"
                              variant="outline"
                              flexShrink={0}
                              onClick={() => restoreCommentVersion(version.text)}
                              disabled={commentLoading}
                            >
                              Restore
                            </Button>
                          </HStack>
                          {version.text && version.text.includes('<') ? (
                            <div
                              className="comment-html-content"
                              style={{ fontSize: '13px', color: '#4b5563', whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(version.text) }}
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.600" whiteSpace="pre-wrap">
                              {version.text}
                            </Text>
                          )}
                        </Box>
                      ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </Dialog.Body>
          <Dialog.Footer borderTopWidth={1} borderColor="gray.100" px={5} py={4}>
            <HStack justifyContent="flex-end" gap={2} w="full">
              <Button variant="ghost" onClick={cancelEditComment} disabled={commentLoading}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleUpdateComment}
                loading={commentLoading}
                disabled={isEmptyHtml(editCommentText)}
              >
                Save
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
    </>
  );
};

export default TaskModal;
